import type { ColumnDef } from "@tanstack/react-table";
import { OrderLineTypes } from "@repo/database";
import {
  formatIntToCurrency,
  generateRandomString,
  highlightText,
} from "@repo/utils";
import { addMilliseconds } from "date-fns";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import type { CartItem } from "~/providers/cart-provider";
import { useProduct } from "~/hooks/use-product";
import { cn } from "~/lib/utils";
import { useCartManager } from "~/managers/use-cart-manager";
import { usePermissionStore } from "~/stores/use-permission-store";
import ItemsTable from "../cash-register/items-table";
import { ProductList } from "../cash-register/product-list";
import { Form, useForm } from "../ui/form";

export default function CartItemExtraForm({
  item,
  onClose,
}: {
  item: CartItem;
  onClose: () => void;
}) {
  const { t } = useTranslation("common");

  const { currentEmployee } = usePermissionStore();

  const form = useForm({
    schema: z.object({
      items: z.array(
        z.object({
          name: z.string().min(1),
          reference: z.string().min(1),
          value: z.number(),
          quantity: z.number(),
          type: z.custom<OrderLineTypes>(),
          parentReference: z.string(),
          createdAt: z.date(),
          vatRate: z.number(),
          originalQuantity: z.number(),
          turnoverGroupName: z.string().optional(),
          turnoverGroupLedgerNumber: z.number().optional(),
          ordered: z.boolean(),
          printed: z.boolean(),
        }),
      ),
    }),
    defaultValues: {
      items: [],
    },
  });

  const { modifiers, modifierGroups } = useProduct();

  const { addToCart } = useCartManager();

  const availableGroups = modifierGroups.filter((group) => {
    if (item.productId === undefined) return true;
    if (item.categoryId) {
      return (
        group.usedByProductCategories.includes(item.categoryId) ||
        group.usedByProducts.includes(item.productId)
      );
    }
    return group.usedByProducts.includes(item.productId);
  });

  const availableModifiers = modifiers.filter((modifier) =>
    availableGroups.some((group) =>
      group.members.some((member) => member === modifier.id),
    ),
  );

  const handleSubmit = form.handleSubmit(async (data) => {
    for (const item of data.items) {
      await addToCart({
        ...item,
        employeeId: currentEmployee?.id,
      });
    }
    onClose();
  });

  const items = useWatch({ control: form.control, name: "items" });

  const columns: ColumnDef<CartItem>[] = [
    {
      id: t("name"),
      accessorKey: "name",
      filterFn: "includesString",
      size: 500,
      cell: ({ row: { original: item }, table }) => {
        const globalFilter = table.getState().globalFilter as string;
        return (
          <div className="flex flex-row items-center gap-1">
            <p
              className={cn(
                "line-clamp-1 text-ellipsis break-all text-start text-sm",
                item.ordered === false && "font-bold",
                item.quantity === 0 && "line-through",
              )}
              dangerouslySetInnerHTML={{
                __html: highlightText(item.name, globalFilter) ?? "",
              }}
            />
          </div>
        );
      },
    },

    {
      id: t("price"),
      accessorKey: "value",
      filterFn: "includesString",
      size: 100,
      cell: ({ row: { original: item }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <>
            {(item.type === OrderLineTypes.product ||
              item.type === OrderLineTypes.extra) && (
              <p
                dangerouslySetInnerHTML={{
                  __html:
                    highlightText(
                      formatIntToCurrency(item.value, false),
                      globalFilter,
                    ) ?? "",
                }}
                className={cn(
                  "text-sm text-right font-bold",
                  item.ordered && "font-light",
                  item.quantity === 0 && "line-through",
                )}
              >
                {}
              </p>
            )}
            {item.type === OrderLineTypes.discount && (
              <p
                className={cn(
                  "text-sm font-bold text-right",
                  item.ordered && "font-light",
                  item.quantity === 0 && "line-through",
                )}
                dangerouslySetInnerHTML={{
                  __html:
                    highlightText(
                      `- ${formatIntToCurrency(item.value, false)}`,
                      globalFilter,
                    ) ?? "",
                }}
              ></p>
            )}
          </>
        );
      },
    },
  ];

  const handleAdd = (newItem: CartItem) => {
    form.setValue("items", [
      ...items,
      {
        ordered: false,
        printed: false,
        name: newItem.name,
        value: newItem.value,
        quantity: 1,
        type: OrderLineTypes.product,
        turnoverGroupName: newItem.turnoverGroupName,
        turnoverGroupLedgerNumber: newItem.turnoverGroupLedgerNumber,
        parentReference: item.reference,
        createdAt: addMilliseconds(item.createdAt, 1),
        reference: generateRandomString(8),
        vatRate: newItem.vatRate,
        originalQuantity: 1,
      },
    ]);
  };

  const handleRemove = (item: CartItem) => {
    form.setValue(
      "items",
      items.filter((i) => i.reference !== item.reference),
    );
  };

  return (
    <Form {...form}>
      <form
        id="cart-item-extra-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-2"
      >
        <div className="flex flex-row gap-2">
          <ItemsTable columns={columns} items={items} onSelect={handleRemove} />
          <div className="flex flex-col min-w-[140px] h-[300px] flex-1 @container">
            <ProductList products={availableModifiers} onClick={handleAdd} />
          </div>
        </div>
      </form>
    </Form>
  );
}
